import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invoicesAPI, invoiceTemplateAPI } from '../services/api';
import { ArrowLeft, Upload, RefreshCw, Mail, ExternalLink, History } from 'lucide-react';

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const templateFileInputRef = useRef(null);
  const [invoice, setInvoice] = useState(null);
  const [versions, setVersions] = useState(null);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [templateUploading, setTemplateUploading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchInvoice();
    fetchVersions();
    fetchTemplateInfo();
  }, [id]);

  const fetchTemplateInfo = async () => {
    try {
      const res = await invoiceTemplateAPI.get();
      setTemplateInfo(res.data);
    } catch {
      setTemplateInfo(null);
    }
  };

  const fetchInvoice = async () => {
    try {
      const response = await invoicesAPI.getOne(id);
      setInvoice(response.data);
    } catch (err) {
      setError('Invoice not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await invoicesAPI.getVersions(id);
      setVersions(response.data);
    } catch (err) {
      setVersions(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (v) => parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const showMsg = (text, isError = false) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 4000);
  };

  const handleGeneratePDF = async () => {
    setActionLoading('generate');
    try {
      const res = await invoicesAPI.generatePDF(id);
      setInvoice((prev) => ({ ...prev, pdf_url: res.data.pdf_url }));
      await fetchVersions();
      showMsg('PDF generated successfully.');
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to generate PDF.', true);
    } finally {
      setActionLoading('');
    }
  };

  const handleUploadPDF = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      showMsg('Please select a PDF file.', true);
      return;
    }
    setActionLoading('upload');
    try {
      const res = await invoicesAPI.uploadPDF(id, file);
      setInvoice((prev) => ({ ...prev, pdf_url: res.data.pdf_url }));
      await fetchVersions();
      showMsg('PDF uploaded successfully.');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to upload PDF.', true);
    } finally {
      setActionLoading('');
    }
  };

  const openPdfInNewTab = async (versionId = null, directPdfUrl = null) => {
    try {
      const res = await invoicesAPI.getPDF(id, versionId ?? undefined);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (directPdfUrl) {
        window.open(directPdfUrl, '_blank', 'noopener');
        showMsg('Opened PDF in new tab. If it doesn’t load, try the link from the client email.');
      } else {
        showMsg(err.response?.data?.detail || 'Failed to load PDF.', true);
      }
    }
  };

  const handleTemplateUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    if (!templateInfo?.has_fillable_pdf && !isPdf) {
      showMsg('Upload a PDF with form fields so we can fill it with customer info each time.', true);
      return;
    }
    if (!isPdf && !/\.(png|jpe?g)$/i.test(file.name)) {
      showMsg('Use a PDF (with form fields) or PNG/JPG.', true);
      return;
    }
    setTemplateUploading(true);
    try {
      await invoiceTemplateAPI.upload(file);
      await fetchTemplateInfo();
      showMsg(isPdf ? 'Template saved. We’ll fill it with customer info each time. Generate PDF and send to customers.' : 'Template uploaded.');
      if (templateFileInputRef.current) templateFileInputRef.current.value = '';
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to upload template.', true);
    } finally {
      setTemplateUploading(false);
    }
  };

  const handleSendEmail = async () => {
    setActionLoading('email');
    try {
      await invoicesAPI.sendEmail(id);
      await fetchInvoice();
      showMsg('Invoice sent to customer by email.');
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to send email.', true);
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-8 h-8 border-2 border-[#0a0a0a] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
        <p className="text-[#737373] mb-6">{error || 'Invoice not found'}</p>
        <button onClick={() => navigate('/invoices')} className="px-4 py-2 bg-[#0a0a0a] text-white rounded-lg font-medium">
          Back to Invoices
        </button>
      </div>
    );
  }

  const lineItems = Array.isArray(invoice.line_items) ? invoice.line_items : (invoice.line_items ? JSON.parse(invoice.line_items) : []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <button
          onClick={() => navigate('/invoices')}
          className="inline-flex items-center gap-2 text-[#525252] hover:text-[#0a0a0a] font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Invoices
        </button>
      </div>

      {message && (
        <div className={`rounded-lg px-4 py-3 text-sm ${message.includes('Failed') ? 'bg-red-50 text-red-800' : 'bg-emerald-50 text-emerald-800'}`}>
          {message}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#e5e5e5] p-6">
        {/* One template per dealer: upload once, we fill it each time */}
        {!templateInfo?.has_fillable_pdf && (
          <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <h3 className="text-sm font-semibold text-amber-900 mb-1">Your invoice template</h3>
            <p className="text-sm text-amber-800 mb-3">Upload <strong>one PDF with form fields</strong>. We use the same template every time and fill in this invoice&apos;s customer and details. Then you generate the PDF and send it to customers.</p>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-900 rounded-lg font-medium hover:bg-amber-200 cursor-pointer disabled:opacity-50">
              <Upload className="w-4 h-4" />
              {templateUploading ? 'Uploading…' : 'Add template'}
              <input
                ref={templateFileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={handleTemplateUpload}
                disabled={templateUploading}
              />
            </label>
          </div>
        )}

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-[#0a0a0a]">{invoice.invoice_number}</h1>
            <p className="text-[#737373] mt-1">{invoice.client_name || '—'} · {invoice.invoice_type}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleGeneratePDF}
              disabled={!!actionLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#f5f5f5] text-[#0a0a0a] rounded-lg font-medium hover:bg-[#e5e5e5] disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${actionLoading === 'generate' ? 'animate-spin' : ''}`} />
              {actionLoading === 'generate' ? 'Generating…' : 'Generate PDF'}
            </button>
            {invoice.pdf_url && (
              <>
                <button
                  type="button"
                  onClick={() => openPdfInNewTab(null, invoice.pdf_url)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a0a0a] text-white rounded-lg font-medium hover:bg-[#262626]"
                >
                  <ExternalLink className="w-4 h-4" />
                  View PDF
                </button>
                <a
                  href={invoice.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#f5f5f5] text-[#0a0a0a] rounded-lg font-medium hover:bg-[#e5e5e5]"
                >
                  Open PDF link
                </a>
              </>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={!!actionLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#f5f5f5] text-[#0a0a0a] rounded-lg font-medium hover:bg-[#e5e5e5] disabled:opacity-50"
            >
              <Upload className={`w-4 h-4 ${actionLoading === 'upload' ? 'animate-pulse' : ''}`} />
              {actionLoading === 'upload' ? 'Uploading…' : 'Upload PDF'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={handleUploadPDF}
            />
          </div>
        </div>

        {/* Send to customer */}
        {invoice.client_email && (
          <div className="mb-6">
            <button
              onClick={handleSendEmail}
              disabled={!!actionLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a0a0a] text-white rounded-lg font-medium hover:bg-[#262626] disabled:opacity-50"
            >
              <Mail className={`w-4 h-4 ${actionLoading === 'email' ? 'animate-pulse' : ''}`} />
              {actionLoading === 'email' ? 'Sending…' : 'Send to customer'}
            </button>
          </div>
        )}

        {/* One template per dealer — we fill it each time, you send to customers */}
        {templateInfo?.has_fillable_pdf && (
          <div className="mt-6 pt-6 border-t border-[#e5e5e5]">
            <h3 className="text-sm font-semibold text-[#0a0a0a] mb-2">Your invoice template</h3>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-[#525252]">Same template for every invoice. We fill in this customer and details. Generate PDF above, then view or send to customer.</p>
              <button
                type="button"
                onClick={() => templateFileInputRef.current?.click()}
                disabled={templateUploading}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
              >
                {templateUploading ? 'Uploading…' : 'Replace template'}
              </button>
              <input
                ref={templateFileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,application/pdf"
                className="hidden"
                onChange={handleTemplateUpload}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm mt-6">
          <div>
            <h3 className="font-semibold text-[#0a0a0a] mb-2">Bill To</h3>
            <p className="text-[#525252]">{invoice.client_name || '—'}</p>
            {invoice.client_address && <p className="text-[#525252]">{invoice.client_address}</p>}
            {invoice.client_email && <p className="text-[#525252]">{invoice.client_email}</p>}
          </div>
          <div>
            <p><span className="text-[#737373]">Issue date:</span> {formatDate(invoice.issue_date)}</p>
            <p><span className="text-[#737373]">Due date:</span> {formatDate(invoice.due_date) || 'Upon receipt'}</p>
            <p><span className="text-[#737373]">Total:</span> <strong>${formatCurrency(invoice.total)}</strong></p>
            <p><span className="text-[#737373]">Status:</span> <strong>{invoice.status}</strong></p>
          </div>
        </div>

        {lineItems.length > 0 && (
          <div className="mt-6">
            <h3 className="font-semibold text-[#0a0a0a] mb-3">Line items</h3>
            <div className="border border-[#e5e5e5] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#fafafa]">
                  <tr>
                    <th className="text-left px-4 py-2 text-[#737373] font-medium">Description</th>
                    <th className="text-right px-4 py-2 text-[#737373] font-medium">Qty</th>
                    <th className="text-right px-4 py-2 text-[#737373] font-medium">Unit price</th>
                    <th className="text-right px-4 py-2 text-[#737373] font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e5e5]">
                  {lineItems.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 text-[#0a0a0a]">{item.description}</td>
                      <td className="px-4 py-2 text-right text-[#525252]">{item.quantity}</td>
                      <td className="px-4 py-2 text-right text-[#525252]">${formatCurrency(item.unit_price)}</td>
                      <td className="px-4 py-2 text-right font-medium text-[#0a0a0a]">${formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {versions && versions.versions && versions.versions.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#e5e5e5] p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0a0a0a] mb-4">
            <History className="w-5 h-5" />
            Version history
          </h2>
          <ul className="space-y-3">
            {versions.versions.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 py-2 border-b border-[#f5f5f5] last:border-0">
                <div>
                  <span className="font-medium text-[#0a0a0a]">Version {v.version_number}</span>
                  <span className="text-[#737373] text-sm ml-2">{v.change_description}</span>
                  <p className="text-xs text-[#a3a3a3] mt-0.5">{formatDate(v.created_at)}</p>
                </div>
                {v.pdf_url && (
                  <button
                    type="button"
                    onClick={() => openPdfInNewTab(v.id, v.pdf_url)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0a0a0a] hover:underline"
                  >
                    View PDF
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetailPage;
